import { AddCircleOutlined, InfoOutlined } from "@mui/icons-material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlined";
import RestoreIcon from "@mui/icons-material/Restore";
import TableRowsOutlined from "@mui/icons-material/TableRowsOutlined";
import { Alert, Box, Tooltip } from "@mui/material";
import type { GridColDef } from "@mui/x-data-grid";
import { type FC, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { JumpingDots } from "../../../../../components/UIToolkit/Animations/JumpingDots";
import { SystemDropdown } from "../../../../../components/UIToolkit/Inputs/SystemDropDown/SystemDropdown";
import { EmptyState } from "../../../../../components/UIToolkit/molecules/EmptyState";
import type { MultiSortOption } from "../../../../../components/UIToolkit/Popups/SystemSortingButtonWithPopup/SystemSortingButtonWithPopup";
import SystemButton from "../../../../../components/UIToolkit/SystemButton/SystemButton";
import { SystemDataGrid } from "../../../../../components/UIToolkit/Tables/SystemDataGrid";
import { useChosenDepartment } from "../../../../../contexts/ChosenDepartment";
import { useDebouncedSave } from "../../../../../hooks/useDebouncedSave";
import { useErrorSnackbar } from "../../../../../hooks/useErrorSnackbar";
import { hrisMappingTimeOffSortingKey } from "../../../../../services/LocalStorageService";
import { HrisIgnoreCategory, PtoTypes, TimeOffReasons, type TimeOffTypeMapping } from "../../../../../utils/models";
import {
	useCreateHrisIgnoredValue,
	useDeleteHrisIgnoredValue,
	useDeleteTimeOffMapping,
	useDepartmentTimeOffTypeMapping,
	useHrisIgnoredValues,
	useHrisPtoTypeStats,
	useHrisRawPtoEventTypes,
	useTimeOffRemapPreviewFetcher,
	useUpsertTimeOffMapping,
} from "./hrisMappingHooks";
import { PtoTypeChangeConfirmDialog } from "./PtoTypeChangeConfirmDialog";
import { type AddCustomFieldSpec, AddCustomMappingButton, HoverActionCell, IgnoredCell } from "./utils";

const EXCLUDED_VALUE = "__excluded__";

export const TimeOffTypeMappingGrid: FC = () => {
	const { t } = useTranslation();
	const { chosenDepartment } = useChosenDepartment();
	const showErrorSnackbar = useErrorSnackbar();
	const departmentId = chosenDepartment.department_id;
	const { data, isLoading, isError } = useDepartmentTimeOffTypeMapping(departmentId);
	const { data: rawPtoTypes, isLoading: isLoadingRaw, isError: isErrorRaw } = useHrisRawPtoEventTypes(departmentId);
	const { data: ptoStats } = useHrisPtoTypeStats(departmentId);
	const fetchRemapPreview = useTimeOffRemapPreviewFetcher(departmentId);
	const upsertMutation = useUpsertTimeOffMapping(departmentId);
	const deleteMutation = useDeleteTimeOffMapping(departmentId);
	const { data: ignoredRows } = useHrisIgnoredValues(departmentId);
	const createIgnoredMutation = useCreateHrisIgnoredValue(departmentId);
	const deleteIgnoredMutation = useDeleteHrisIgnoredValue(departmentId);
	const [pendingKeys, setPendingKeys] = useState<Set<string>>(new Set());
	// Shared open state so the toolbar button and the in-grid EmptyState action drive the same dialog.
	const [addDialogOpen, setAddDialogOpen] = useState(false);
	// Pending PTO type change awaiting confirmation when the raw type has synced time-offs in the last 60 days.
	const [ptoTypeChangeTarget, setPtoTypeChangeTarget] = useState<{ row: TimeOffTypeMapping; ptoType: string; count: number } | null>(null);

	// Last-60-days PTO counts keyed by normalized raw event_type (matching the backend's trim+lower key).
	const countByRaw = useMemo(() => new Map((ptoStats ?? []).map((s) => [s.rawString, s.countLast60])), [ptoStats]);
	const getCount = useCallback((rawString: string) => countByRaw.get(rawString.trim().toLowerCase()) ?? 0, [countByRaw]);
	// A missing count is only a real zero once stats have loaded; while loading/failed it's "unknown", so we
	// must not treat it as "nothing to re-map" and skip the confirm dialog (DAT-200).
	const statsReady = ptoStats !== undefined;

	const timeOffIgnoreByRaw = useMemo(() => {
		const m = new Map<string, { id: number; isGlobal: boolean }>();
		for (const row of ignoredRows ?? []) {
			if (row.category !== HrisIgnoreCategory.TimeOffType) continue;
			m.set(row.rawValue.toLowerCase(), { id: row.id, isGlobal: row.departmentId === null });
		}
		return m;
	}, [ignoredRows]);

	// Lowercased set of PTO event_type codes present in raw HRIS data for this department.
	const rawPtoTypeSet = useMemo(() => {
		const s = new Set<string>();
		for (const code of rawPtoTypes ?? []) {
			const v = code.trim().toLowerCase();
			if (v) s.add(v);
		}
		return s;
	}, [rawPtoTypes]);

	// Existing mapping keys (trim+lower, matching the backend `raw_string.strip().lower()`), and the
	// raw codes that have no mapping at all — they drive the banner, synthetic rows, and cell rendering.
	const mappingKeySet = useMemo(() => new Set((data ?? []).map((m) => m.rawString.trim().toLowerCase())), [data]);
	const unmappedKeySet = useMemo(() => new Set([...rawPtoTypeSet].filter((k) => !mappingKeySet.has(k))), [rawPtoTypeSet, mappingKeySet]);
	const orphanCount = unmappedKeySet.size;
	// Null-safe: skeleton placeholder rows (no `rawString`) shown while loading are never unmapped rows.
	const isRowUnmapped = useCallback((row?: TimeOffTypeMapping) => !!row?.rawString && unmappedKeySet.has(row.rawString.trim().toLowerCase()), [unmappedKeySet]);

	// Surface raw-PTO fetch failures instead of silently rendering an empty orphan set — without
	// blocking the grid, since the existing mappings (from `data`) are still usable.
	useEffect(() => {
		if (isErrorRaw) showErrorSnackbar(t("hris.raw_pto.load.failed"), {});
	}, [isErrorRaw, showErrorSnackbar, t]);

	const handleToggleIgnore = useCallback(
		(rawString: string) => {
			const existing = timeOffIgnoreByRaw.get(rawString.toLowerCase());
			if (existing) {
				if (existing.isGlobal) return;
				deleteIgnoredMutation.mutate(existing.id, { onError: (err) => showErrorSnackbar(t("hris.mapping.deleteError"), {}, err) });
				return;
			}
			createIgnoredMutation.mutate(
				{ category: HrisIgnoreCategory.TimeOffType, rawValue: rawString, departmentId },
				{ onError: (err) => showErrorSnackbar(t("hris.mapping.saveError"), {}, err) },
			);
		},
		[createIgnoredMutation, deleteIgnoredMutation, departmentId, showErrorSnackbar, t, timeOffIgnoreByRaw],
	);

	const toSortOptions: MultiSortOption<TimeOffTypeMapping>[] = useMemo(
		() => [
			{ label: t("hris.mapping.column.rawString"), sortColumns: ["rawString"] },
			{ label: t("hris.mapping.column.reason"), sortColumns: ["reason"] },
			// PTO Type is rendered via a custom dropdown (sortable: false), so it's not offered as a sort key.
			{ label: t("hris.mapping.column.isOverride"), sortColumns: ["isOverride"] },
			{ label: t("hris.mapping.column.isCustom"), sortColumns: ["isCustom"] },
		],
		[t],
	);

	const scheduleSave = useDebouncedSave<{ rawString: string; ptoType: string | null; reason?: string | null; remapPrevious?: boolean }>((payload) => {
		upsertMutation.mutate(payload, {
			onError: (err) => showErrorSnackbar(t("hris.mapping.saveError"), {}, err),
			onSettled: () =>
				setPendingKeys((prev) => {
					const next = new Set(prev);
					next.delete(payload.rawString);
					return next;
				}),
		});
	});

	const handleDelete = useCallback(
		(rawString: string) => {
			deleteMutation.mutate(rawString, { onError: (err) => showErrorSnackbar(t("hris.mapping.deleteError"), {}, err) });
		},
		[deleteMutation, showErrorSnackbar, t],
	);

	const excludedLabel = t("hris.mapping.excluded");
	const notMappedLabel = t("hris.mapping.notMapped");

	const toAddFields: AddCustomFieldSpec[] = useMemo(
		() => [
			{
				name: "ptoType",
				label: t("hris.mapping.column.ptoType"),
				options: [...Object.values(PtoTypes).map((v) => ({ value: v, label: v })), { value: EXCLUDED_VALUE, label: excludedLabel }],
			},
			{ name: "reason", label: t("hris.mapping.column.reason"), options: Object.values(TimeOffReasons).map((v) => ({ value: v, label: v })) },
		],
		[t, excludedLabel],
	);

	const handleAddCustom = useCallback(
		(key: string, extras: Record<string, string>) => {
			const isExcluded = extras.ptoType === EXCLUDED_VALUE;
			upsertMutation.mutate(
				{ rawString: key, ptoType: isExcluded ? null : extras.ptoType, reason: isExcluded ? null : extras.reason || null },
				{ onError: (err) => showErrorSnackbar(t("hris.mapping.saveError"), {}, err) },
			);
		},
		[upsertMutation, showErrorSnackbar, t],
	);

	const saveTimeOff = useCallback(
		(row: TimeOffTypeMapping, ptoType: string | null, remapPrevious: boolean) => {
			setPendingKeys((prev) => new Set(prev).add(row.rawString));
			scheduleSave(row.rawString, {
				rawString: row.rawString,
				ptoType,
				reason: ptoType === null ? null : (row.reason ?? null),
				...(remapPrevious ? { remapPrevious } : {}),
			});
		},
		[scheduleSave],
	);

	// PTO Type is saved straight from its dropdown (EXCLUDED_VALUE → null "Do not sync"); choosing one
	// also maps an unmapped row. Reason is cleared when the row is set to "Do not sync". When the change
	// is to a real type and synced time-offs exist in the last 60 days, confirm first (DAT-200) so the
	// admin can also re-map history; otherwise there's nothing to re-map, so save going forward.
	const handlePtoTypeChange = useCallback(
		async (row: TimeOffTypeMapping, value?: string) => {
			if (value === undefined) return;
			const ptoType = value === EXCLUDED_VALUE ? null : value;
			// "Do not sync" has no target type to re-map to. A zero count means nothing to confirm — but only
			// trust that once stats have loaded; while they're loading/failed, fall through to the authoritative
			// preview fetch instead of silently skipping the re-map confirm.
			if (ptoType === null || (statsReady && getCount(row.rawString) === 0)) {
				saveTimeOff(row, ptoType, false);
				return;
			}
			// The dialog's "you will change X time offs" must reflect the actual external_time_off records the
			// re-map will touch — fewer than the 60-day raw-event volume, since consecutive events merge into
			// spans. Fall back to the raw count if the preview fetch fails so the dialog still opens.
			let count = getCount(row.rawString);
			try {
				count = await fetchRemapPreview(row.rawString);
			} catch (err) {
				showErrorSnackbar(t("hris.mapping.remapPreview.error"), {}, err as Error);
			}
			// Nothing to re-map after all (e.g. stats hadn't loaded when we checked) — save going forward.
			if (count === 0) {
				saveTimeOff(row, ptoType, false);
				return;
			}
			setPtoTypeChangeTarget({ row, ptoType, count });
		},
		[getCount, statsReady, saveTimeOff, fetchRemapPreview, showErrorSnackbar, t],
	);

	const handleConfirmPtoTypeChange = useCallback(
		(remapPrevious: boolean) => {
			if (!ptoTypeChangeTarget) return;
			saveTimeOff(ptoTypeChangeTarget.row, ptoTypeChangeTarget.ptoType, remapPrevious);
			setPtoTypeChangeTarget(null);
		},
		[ptoTypeChangeTarget, saveTimeOff],
	);

	const processRowUpdate = useCallback(
		(newRow: TimeOffTypeMapping, oldRow: TimeOffTypeMapping): TimeOffTypeMapping => {
			// PTO Type saves via its dropdown; here we only persist Reason edits on already-mapped rows.
			// Skip rows that are not yet mapped or set to "Do not sync" (null ptoType carries no reason).
			if (isRowUnmapped(newRow) || newRow.ptoType === null || newRow.reason === oldRow.reason) return newRow;
			setPendingKeys((prev) => new Set(prev).add(newRow.rawString));
			scheduleSave(newRow.rawString, { rawString: newRow.rawString, ptoType: newRow.ptoType, reason: newRow.reason ?? null });
			return newRow;
		},
		[scheduleSave, isRowUnmapped],
	);

	const reasonOptions = useMemo(() => ["", ...Object.values(TimeOffReasons)], []);
	const ptoTypeItems = useMemo(
		() => [...Object.values(PtoTypes).map((v) => ({ value: v, label: v })), { value: EXCLUDED_VALUE, label: excludedLabel }],
		[excludedLabel],
	);

	// Custom rows are ALWAYS visible; other mapping rows must appear in raw PTO codes (`isCustom` rows
	// render with `isOverride: false` to hide the restore icon). Unmapped raw codes are appended as
	// synthetic in-memory rows so admins can map them inline — they're not part of `data`.
	const rows = useMemo(() => {
		const mapped = (data ?? [])
			.filter((r) => r.isCustom || rawPtoTypeSet.has(r.rawString.trim().toLowerCase()))
			.map((r) => (r.isCustom ? { ...r, isOverride: false } : r));
		// Dedupe synthetic rows by normalized key: the backend DISTINCT is exact-match, so case-only
		// variants (e.g. "Vacation" / "vacation") must not produce two rows for one orphan key.
		const synthetic: TimeOffTypeMapping[] = [];
		const seen = new Set<string>();
		for (const code of rawPtoTypes ?? []) {
			const key = code.trim().toLowerCase();
			if (!key || mappingKeySet.has(key) || seen.has(key)) continue;
			seen.add(key);
			synthetic.push({ rawString: code, ptoType: null, reason: null, isOverride: false, isCustom: false, defaultDisplay: null });
		}
		return [...mapped, ...synthetic];
	}, [data, rawPtoTypes, rawPtoTypeSet, mappingKeySet]);

	const columns: GridColDef<TimeOffTypeMapping>[] = useMemo(
		() => [
			{
				field: "rawString",
				headerName: t("hris.mapping.column.rawString"),
				flex: 1,
				minWidth: 200,
			},
			{
				field: "reason",
				headerName: t("hris.mapping.column.reason"),
				flex: 1,
				minWidth: 160,
				editable: true,
				type: "singleSelect",
				valueOptions: reasonOptions,
			},
			{
				field: "ptoType",
				headerName: t("hris.mapping.column.ptoType"),
				flex: 1,
				minWidth: 160,
				sortable: false,
				// Always-visible dropdown so the cell reads as editable. Unmapped rows have no selection and
				// show a "Not mapped" placeholder; mapped rows show their PTO type (or "Do not sync" for null).
				renderCell: (params) => {
					const selected = isRowUnmapped(params.row) ? undefined : (params.row.ptoType ?? EXCLUDED_VALUE);
					return (
						<SystemDropdown<string>
							items={ptoTypeItems}
							selectedItem={selected}
							onChange={(value) => void handlePtoTypeChange(params.row, value)}
							renderValue={selected ? undefined : () => <em style={{ opacity: 0.6 }}>{notMappedLabel}</em>}
							displayEmpty
							formControlSx={{ minWidth: 150, my: 0.5 }}
							data-testid={`hris-mapping-pto-type-${params.row.rawString}`}
						/>
					);
				},
			},
			{
				field: "countLast60",
				headerName: t("hris.mapping.column.last60Days"),
				width: 120,
				align: "center",
				headerAlign: "center",
				sortable: false,
				renderCell: (params) => getCount(params.row.rawString),
			},
			{
				field: "isOverride",
				headerName: t("hris.mapping.column.isOverride"),
				width: 100,
				align: "center",
				headerAlign: "center",
				display: "flex",
				renderCell: (params) =>
					pendingKeys.has(params.row.rawString) ? (
						<JumpingDots />
					) : (
						<HoverActionCell
							show={params.row.isOverride && !params.row.isCustom}
							actionIcon={<RestoreIcon fontSize="small" />}
							tooltip={params.row.defaultDisplay ? `${t("hris.mapping.resetToDefault")}: ${params.row.defaultDisplay}` : t("hris.mapping.resetToDefault")}
							onAction={() => handleDelete(params.row.rawString)}
						/>
					),
			},
			{
				field: "isCustom",
				headerName: t("hris.mapping.column.isCustom"),
				width: 100,
				align: "center",
				headerAlign: "center",
				display: "flex",
				renderCell: (params) => (
					<HoverActionCell
						show={params.row.isCustom}
						actionIcon={<DeleteOutlineIcon fontSize="small" color="error" />}
						tooltip={t("hris.mapping.deleteCustom")}
						onAction={() => handleDelete(params.row.rawString)}
					/>
				),
			},
			{
				field: "ignored",
				headerName: t("hris.mapping.column.ignored"),
				width: 100,
				align: "center",
				headerAlign: "center",
				display: "flex",
				sortable: false,
				renderCell: (params) => (
					<IgnoredCell
						ignore={timeOffIgnoreByRaw.get(params.row.rawString.toLowerCase())}
						rawValue={params.row.rawString}
						onToggle={() => handleToggleIgnore(params.row.rawString)}
					/>
				),
			},
		],
		[
			t,
			reasonOptions,
			ptoTypeItems,
			notMappedLabel,
			isRowUnmapped,
			handlePtoTypeChange,
			handleDelete,
			handleToggleIgnore,
			timeOffIgnoreByRaw,
			pendingKeys,
			getCount,
		],
	);

	return (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 1, height: "100%", minHeight: 0 }}>
			{orphanCount > 0 && (
				<Alert severity="info" data-testid="hris-mapping-time-off-orphan-banner" sx={{ alignItems: "center" }}>
					{t("hris.mapping.orphans.banner.timeOff", { count: orphanCount })}
				</Alert>
			)}
			{/* Fill the tall TimeOff container (flex:1), not content — containedHeight is for sparse standalone grids and conflicts with a fill parent. */}
			<Box sx={{ flex: 1, minHeight: 0 }}>
				<SystemDataGrid<TimeOffTypeMapping>
					rows={rows}
					columns={columns}
					getRowId={(row) => row.rawString}
					// Reason only persists on already-mapped rows, so don't let it be edited on unmapped or
					// "Do not sync" (null ptoType) rows — keeps editability consistent with what gets saved.
					isCellEditable={(params) => !(params.field === "reason" && (isRowUnmapped(params.row) || params.row.ptoType === null))}
					searchableColumns={["rawString"]}
					searchPlaceholder={t("hris.mapping.search.placeholder.rawString")}
					sortingOptions={toSortOptions}
					defaultSortState={[{ sortColumns: ["rawString"], sortOrder: "asc" }]}
					localStorageKey={hrisMappingTimeOffSortingKey}
					processRowUpdate={processRowUpdate}
					loading={isLoading || isLoadingRaw}
					error={isError}
					emptyState={
						<EmptyState
							icon={<TableRowsOutlined />}
							title={t("hris.mapping.grid.empty.title")}
							hint={t("hris.mapping.grid.empty.hint")}
							action={
								<SystemButton
									buttonVariant="secondary"
									size="small"
									leadingIcon={<AddCircleOutlined fontSize="small" />}
									onClick={() => setAddDialogOpen(true)}
									data-testid="hris-mapping-time-off-empty-add"
								>
									{t("hris.mapping.addCustom")}
								</SystemButton>
							}
						/>
					}
					leadingToolbarNodes={
						<AddCustomMappingButton
							onAdd={handleAddCustom}
							keyLabel={t("hris.mapping.column.rawString")}
							extraFields={toAddFields}
							open={addDialogOpen}
							onOpenChange={setAddDialogOpen}
							hideTriggerButton={false}
						/>
					}
					trailingToolbarNodes={
						<Tooltip title={t("hris.mapping.addCustom.tooltip")}>
							<InfoOutlined />
						</Tooltip>
					}
					dataTestId="hris-mapping-time-off"
				/>
			</Box>
			{ptoTypeChangeTarget && (
				<PtoTypeChangeConfirmDialog
					key={ptoTypeChangeTarget.row.rawString}
					open
					rawString={ptoTypeChangeTarget.row.rawString}
					currentPtoTypeLabel={ptoTypeChangeTarget.row.ptoType ?? excludedLabel}
					ptoTypeLabel={ptoTypeChangeTarget.ptoType}
					countLast60={ptoTypeChangeTarget.count}
					onConfirm={handleConfirmPtoTypeChange}
					onClose={() => setPtoTypeChangeTarget(null)}
				/>
			)}
		</Box>
	);
};
